import { useState, useMemo, useCallback } from 'react'
import { TextMatch } from 'shared/text'

import {
  Stack, ToggleButtonGroup, ToggleButton, Chip
} from "@mui/material"
import { DashBoardIcon, OrganIcon, ListIcon } from 'shared/icons'

import { Loading } from 'components/controls/loading'
import { ErrorInfo } from 'components/controls/errorInfo'
import { SearchWordField } from 'components/controls/searchWordField'
import { SwitchBox } from 'components/controls/switchBox'

import { useSearch } from 'features/search/context'

import { KojinResult, useListKojin } from "interface/kojin"
import { SisetuResult } from 'interface/sisetu'

import { SisetuPreviewDoctorsDashBoard } from './dashBoard'
import { SisetuPreviewDoctorsOrgan } from './organ'
import { SisetuPreviewDoctorsTable } from './table'
import { SisetuDoctorsCondition, SisetuDoctorsConditions, SisetuDoctorsConditionsAxis, SisetuDoctorsNenreiCondition } from './condition'
import { useSisetuPreview } from '../../container'

let refConditions: SisetuDoctorsConditions | undefined = undefined

type SisetuPreviewDoctorsPageProps = {
  sisetu: SisetuResult
  container: ReturnType<typeof useSisetuPreview>
}
export const SisetuPreviewDoctorsPage = ({
  sisetu,
  container: {
    onSisetuOpen,
    onKojinOpen
  }
}: SisetuPreviewDoctorsPageProps) => {
  const search = useSearch()

  const [panelMode, setPanelMode] = useState<string>('dashboard')
  const [searchWord, setSearchWord] = useState<string>("")
  const [isKOLOnly, setIsKOLOnly] = useState<boolean>(false)
  const [conditions, setConditions] = useState<SisetuDoctorsConditions>({})

  const listState = useListKojin(search?.searchId, { sisetuCode: sisetu.sisetuCode }, { updateTime: search?.updateTime })
  const loadedProgress = useMemo(
    () => listState.loaded && listState.total ? (listState.loaded / listState.total) * 100 : undefined,
    [listState]
  )

  const filteredList = useMemo<KojinResult[] | null>(
    () => {
      if (!listState.data) return null
      if (searchWord !== '') {
        const match = new TextMatch(searchWord)
        return listState.data.filter(kojin => {
          if (isKOLOnly && kojin.score?.rank === undefined) return false
          return match.match(kojin.isiName, kojin.isiNameKana)
        })
      } else if (isKOLOnly) {
        return listState.data.filter(kojin => {
          if (kojin.score?.rank === undefined) return false
          return true
        })
      } else {
        return listState.data
      }
    },
    [listState.data, searchWord, isKOLOnly]
  )

  const onChangePanelMode = useCallback(
    (event: React.MouseEvent<HTMLElement>, value: string) => {
      if (value && value.length) {
        setPanelMode(value)
      }
    },
    []
  )

  // note:
  // conditionsに依存するとそのたびにこの関数が再構築されてしまい、無駄な再描画が発生してしまうため
  refConditions = conditions
  const onChangeCondition = useCallback(
    (
      axisKey: SisetuDoctorsConditionsAxis,
      condition?: SisetuDoctorsCondition | SisetuDoctorsNenreiCondition
    ) => {
      const clone: SisetuDoctorsConditions = refConditions ? { ...refConditions } : {}
      if (condition) {
        if (axisKey === "nenrei") {
          const c = condition as SisetuDoctorsNenreiCondition
          if ((
            clone.nenrei?.start === c.start &&
            clone.nenrei?.last === c.last &&
            clone.nenrei?.seibetu === c.seibetu
          )) {
            delete clone[axisKey]
          } else {
            clone[axisKey] = c
          }
        } else {
          const c = condition as SisetuDoctorsCondition
          if (clone[axisKey]?.code === c.code) {
            delete clone[axisKey]
          } else {
            clone[axisKey] = c
          }
        }
      } else {
        delete clone[axisKey]
      }
      setConditions(clone)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )
  
  return (
    <Stack
      direction="column"
      sx={{
        maxHeight: '100%',
        height: '100%',
        maxWidth: '100%',
        width: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden'
      }}
    >
      { listState.loading && <Loading percent={loadedProgress}/> }
      { listState.error && <ErrorInfo error={listState.error} padding={1}/> }

      <Stack
        direction="row"
        padding={1}
        alignItems='center'
        flexWrap="wrap"
        useFlexGap
        spacing={1}
        justifyContent="space-between"
        sx={{
          borderBottom: 1,
          borderColor: 'divider'
        }}
      >
        <ToggleButtonGroup 
          sx={{ flexShrink: 0 }}
          color="primary"
          value={panelMode}
          exclusive
          onChange={onChangePanelMode}
          aria-label="panel-mode"
          size="small"
        >
          <ToggleButton value="dashboard"><DashBoardIcon/>&emsp;分析&emsp;</ToggleButton>
          <ToggleButton value="organ"><OrganIcon/>&emsp; 組織&emsp;</ToggleButton>
          <ToggleButton value="list"><ListIcon/>&emsp; 一覧&emsp;</ToggleButton>
        </ToggleButtonGroup>

        <Stack
          flexGrow={1}
          direction='row'
          spacing={0.5}
          flexWrap="wrap"
          useFlexGap
          justifyContent="flex-end"
        >
          {(Object.keys(conditions) as SisetuDoctorsConditionsAxis[]).map((key, i) => (
            <Chip key={`condition-${i}-${key}`} label={conditions[key]?.name || ''} variant="outlined" onDelete={()=> onChangeCondition(key)} />
          ))}
        </Stack>

        <Stack
          direction='row'
          marginLeft={1}
          spacing={0.5}
          justifyContent="flex-end"
        >
          {(search?.kojinCount ?? 0) > 0 && (
            <SwitchBox
              label="KOLのみ"
              checked={isKOLOnly}
              labelPlacement="start"
              onChange={(event) => setIsKOLOnly(event.target.checked)}
            />
          )}
          <SearchWordField
            id='search-word-kojin'
            fullWidth
            searchWord={searchWord}
            onChangeSearchWord={setSearchWord}
          />
        </Stack>
      </Stack>

      { panelMode === 'dashboard' ? (
        <SisetuPreviewDoctorsDashBoard
          sisetuCode={sisetu.sisetuCode}
          kojinList={filteredList || undefined}
          condition={conditions}
          onChangeCondition={onChangeCondition}
          onSisetuOpen={onSisetuOpen}
          sx={{
            flexGrow: 1,
            overflow: 'auto',
            position: 'relative',
            background: 'GhostWhite'
          }}
        />
      ) : panelMode === 'organ' ? (
        <SisetuPreviewDoctorsOrgan
          sisetuCode={sisetu.sisetuCode}
          kojinList={filteredList || undefined}
          onKojinOpen={onKojinOpen}
          condition={conditions}
          onChangeCondition={onChangeCondition}
          sx={{
            flexGrow: 1,
            overflow: 'hidden',
            position: 'relative'
          }}
        />
      ) : panelMode === 'list' ? (
        <SisetuPreviewDoctorsTable
          sisetuCode={sisetu.sisetuCode}
          kojinList={filteredList || undefined}
          condition={conditions}
          onKojinOpen={onKojinOpen}
          sx={{
            flexGrow: 1,
            overflow: 'hidden',
            position: 'relative'
          }}
        />
      ) : <></> }
    </Stack>
  )
}